/**
 * Selection + ranking for the weekly review deck ("Ukens gjennomgang").
 *
 * The deck exists because a dashboard is infinitely available and therefore
 * never *due*, while a finite stack of cards that ends is something you can
 * finish. What makes it worth building here rather than anywhere else: every
 * category the user assigns to a bank row feeds `LearnMerchantCategory` (the
 * expenses afterUpdate hook), so the next nightly sync categorizes that
 * merchant by itself. The deck shrinks week over week *because* it was used.
 *
 * Selection is deliberately "rows that need a decision", not "rows not yet
 * reviewed". An auto-categorized bank row never enters the deck at all, which
 * is what makes the shrinking visible. `reviewed` only drops what has already
 * been handled — including rows the user deliberately left uncategorized,
 * which would otherwise come back every single week.
 *
 * Kept dependency-free (like expenseFilters/budgetVsActual) so the math is
 * unit-testable without Vue or store mocks.
 */

import type { FirestoreCategory, FirestoreExpense } from "../types/firestore"
import { isPlanningTemplate } from "./budgetVsActual"
import { BANK_SOURCE } from "./expenseFilters"

/** Why a row is in the deck. Also decides which controls the card shows. */
export type ReviewReason =
  /** duplicateOf is set — the user must confirm or clear it. */
  | "suspected_duplicate"
  /** Bank row whose description looks like money moved between own accounts. */
  | "possible_transfer"
  /** Bank row with no category that resolves. */
  | "uncategorized_bank"
  /** Manual entry with no category that resolves. */
  | "uncategorized_manual"

export interface ReviewCard {
  expense: FirestoreExpense
  reason: ReviewReason
}

export interface ReviewQueueOptions {
  /** Only consider rows this recent. Default 30 days. */
  withinDays?: number
  /** Cap the deck so a first 90-day import isn't a 400-card wall. Default 50. */
  maxCards?: number
  /** "Today", injectable for tests. Default: now. */
  today?: Date
}

export interface ReviewQueue {
  cards: ReviewCard[]
  /** How many rows qualified before `maxCards` truncation. */
  totalPending: number
}

/**
 * Conservative transfer tells. Norwegian banks name their own sweeps:
 * DNB writes "Overføring Mobilbank" and "Kontoregulering".
 *
 * This only ever *suggests* — the card asks, the user answers. Deliberately
 * so: for an envelope-account setup where the main account isn't linked,
 * pulling money out of a category account IS the purchase, and auto-marking
 * those by description would erase the spending record. That mistake is
 * already documented in docs/FEATURES.md; this is the safe half of it.
 */
const TRANSFER_HINT = /(overf[øo]ring|kontoregulering|egen konto|egne kont(oer|i)|intern overf)/i

/** Priority order — lower sorts first. */
const REASON_RANK: Record<ReviewReason, number> = {
  // Highest stakes: a duplicateOf row is silently excluded from every total
  // right now. Until someone rules on it, the month's spend is a guess.
  suspected_duplicate: 0,
  // Next, because saying "yes, transfer" removes the need to categorize at all.
  possible_transfer: 1,
  uncategorized_bank: 2,
  uncategorized_manual: 3,
}

function isBankRow(expense: FirestoreExpense): boolean {
  return expense.source === BANK_SOURCE
}

/** "YYYY-MM-DD" for a date, matching the storage format of expense.date. */
function dateKey(date: Date): string {
  const m = date.getMonth() + 1
  const d = date.getDate()
  return `${date.getFullYear()}-${m < 10 ? `0${m}` : m}-${d < 10 ? `0${d}` : d}`
}

/**
 * Why this row needs a human, or null when it doesn't.
 *
 * Order matters: a row can be several of these at once, and the first match
 * decides which question the card asks.
 */
export function reviewReasonFor(
  expense: FirestoreExpense,
  resolvesCategory: (ref: string) => boolean,
): ReviewReason | null {
  // Plans are not things that happened; their generated occurrences carry the
  // actual spend and get reviewed instead.
  if (isPlanningTemplate(expense)) return null
  // Already handled — terminal, however the row looks now.
  if (expense.reviewed) return null
  // Income has no category in this app, so there is nothing to decide about it.
  if (expense.type !== "expense") return null

  if (expense.duplicateOf) return "suspected_duplicate"

  // A row already marked as a transfer is settled: it's out of the totals and
  // its category is irrelevant.
  if (expense.isTransfer) return null

  if (isBankRow(expense) && TRANSFER_HINT.test(expense.description || "")) {
    return "possible_transfer"
  }

  const categorized = Boolean(expense.category) && resolvesCategory(expense.category)
  if (categorized) return null

  return isBankRow(expense) ? "uncategorized_bank" : "uncategorized_manual"
}

/**
 * Build the deck.
 *
 * Sorted by reason priority, then by amount descending — the deck should
 * front-load what actually moves the month's numbers, rather than walking
 * chronologically through a week of 40-kr coffees before reaching the
 * 12 000-kr row that was miscategorized.
 */
export function buildReviewQueue(
  expenses: FirestoreExpense[],
  categories: FirestoreCategory[],
  options: ReviewQueueOptions = {},
): ReviewQueue {
  const { withinDays = 30, maxCards = 50, today = new Date() } = options

  const known = new Set<string>()
  for (const c of categories) {
    known.add(c.id)
    known.add(c.name)
  }
  const resolvesCategory = (ref: string) => known.has(ref)

  const cutoff = new Date(today)
  cutoff.setHours(0, 0, 0, 0)
  cutoff.setDate(cutoff.getDate() - withinDays)
  const cutoffKey = dateKey(cutoff)

  const cards: ReviewCard[] = []
  for (const expense of expenses) {
    // Date strings are "YYYY-MM-DD", so lexicographic compare is chronological.
    if (!expense.date || expense.date < cutoffKey) continue
    const reason = reviewReasonFor(expense, resolvesCategory)
    if (reason) cards.push({ expense, reason })
  }

  cards.sort((a, b) => {
    const byReason = REASON_RANK[a.reason] - REASON_RANK[b.reason]
    if (byReason !== 0) return byReason
    return b.expense.amount - a.expense.amount
  })

  return { cards: cards.slice(0, maxCards), totalPending: cards.length }
}

/**
 * How many rows are waiting. Same predicate as the deck — the dashboard card
 * must never promise a count the deck doesn't then contain.
 */
export function countPendingReview(
  expenses: FirestoreExpense[],
  categories: FirestoreCategory[],
  options: ReviewQueueOptions = {},
): number {
  return buildReviewQueue(expenses, categories, options).totalPending
}

/**
 * Collapse a bank description to a stable merchant key.
 *
 * A deliberate mirror of `NormalizeMerchant` in
 * `backend/internal/categorize/categorize.go` — same lowercasing, same
 * card-processor separators, same numeric-token drop, same 80-char cap. Keep
 * the two in sync: the deck suggests with this, the server learns with that,
 * and a divergence would show up as the deck proposing a category the next
 * sync then disagrees with.
 *
 * Why it can't just reuse `useCategorySuggestion`: that matches descriptions
 * exactly, which is right for hand-typed entries and useless for bank rows —
 * "REMA 1000 AVD 123" and "REMA 1000 AVD 456" are the same shop and never
 * match as strings.
 */
export function normalizeMerchant(description: string): string {
  const collapsed = (description || "")
    .toLowerCase()
    .replace(/[*_/#|]+/g, " ")
    .replace(/\b\d[\d.,:-]*\b/g, " ")
    .replace(/\s+/g, " ")
    .trim()
  return collapsed.length > 80 ? collapsed.slice(0, 80) : collapsed
}

/**
 * merchant key → category id, learned from the user's own already-categorized
 * history. The most recent row wins, so a recategorization propagates to the
 * rest of that merchant's cards immediately.
 */
export function buildMerchantSuggestionIndex(
  expenses: FirestoreExpense[],
  categories: FirestoreCategory[],
): Map<string, string> {
  const idByRef = new Map<string, string>()
  for (const c of categories) {
    idByRef.set(c.id, c.id)
    idByRef.set(c.name, c.id)
  }

  const index = new Map<string, { categoryId: string; date: string }>()
  for (const expense of expenses) {
    if (expense.type !== "expense") continue
    if (isPlanningTemplate(expense)) continue
    const categoryId = idByRef.get(expense.category)
    if (!categoryId) continue
    const key = normalizeMerchant(expense.description)
    if (!key) continue
    const existing = index.get(key)
    if (!existing || expense.date >= existing.date) {
      index.set(key, { categoryId, date: expense.date })
    }
  }

  const flat = new Map<string, string>()
  for (const [key, value] of index) flat.set(key, value.categoryId)
  return flat
}

/** Suggested category id for a description, or null when nothing matches. */
export function suggestCategoryForMerchant(index: Map<string, string>, description: string): string | null {
  const key = normalizeMerchant(description)
  if (!key) return null
  return index.get(key) ?? null
}

/** Whole days since an ISO timestamp, or null when it's absent/unparseable. */
export function daysSince(iso: string | null | undefined, today: Date = new Date()): number | null {
  if (!iso) return null
  const then = new Date(iso)
  if (Number.isNaN(then.getTime())) return null
  const ms = today.getTime() - then.getTime()
  if (ms < 0) return 0
  return Math.floor(ms / 86_400_000)
}
